import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { sentencesApi } from '../api/client';
import { useAuthStore, isAdmin } from '../store/authStore';
import { useGameStore } from '../store/gameStore';

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '8px',
  color: '#e2e8f0',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: '4px',
  fontSize: '0.8rem',
  color: '#94a3b8',
};

const btn = (bg, color = 'white') => ({
  padding: '8px 16px',
  background: bg,
  color,
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
});

const EMPTY_FORM = { chinese: '', pinyin: '', english: '', notes: '' };

// Strip punctuation/whitespace so the tile breakdown only contains characters.
const STRIP_RE = /[，。！？、；：""''（）\s]/g;

// Build the breakdown for a sentence, reusing any saved per-character info.
const buildBreakdown = (sentence) => {
  const chars = Array.from(sentence.chinese.replace(STRIP_RE, ''));
  const saved = Array.isArray(sentence.breakdown) ? sentence.breakdown : [];
  return chars.map((char, idx) => {
    // Prefer the saved entry at the same index if its char matches, else match by char.
    const savedEntry = saved[idx]?.char === char ? saved[idx] : saved.find((b) => b?.char === char);
    return {
      char,
      pinyin: savedEntry?.pinyin || '',
      radical: savedEntry?.radical || '',
      radical_meaning: savedEntry?.radical_meaning || '',
      meaning: savedEntry?.meaning || '',
    };
  });
};

// Convert a user sentence into a game lesson sentence (breakdown from chars)
export const userSentencesToLesson = (sentences) => ({
  code: 'MY-SENTENCES',
  title: 'My Sentences',
  level: 'Custom',
  audio: null,
  vocabulary: [],
  dialogue: sentences.map((s, i) => ({
    id: s.id ?? i,
    sentenceId: s.id ?? null,
    speaker: 'Me',
    chinese: s.chinese,
    pinyin: s.pinyin || '',
    english: s.english,
    breakdown: buildBreakdown(s),
  })),
});

const SentenceForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.chinese.trim() || !form.english.trim()) return;
    onSave(form);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: '#1e293b',
        border: '1px solid #6366f1',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div>
        <label style={labelStyle}>Chinese * </label>
        <input style={{ ...inputStyle, fontSize: '1.2rem' }} value={form.chinese} onChange={set('chinese')} placeholder="我喜欢学习。" required />
      </div>
      <div>
        <label style={labelStyle}>Pinyin</label>
        <input style={inputStyle} value={form.pinyin} onChange={set('pinyin')} placeholder="Wǒ xǐhuān xuéxí." />
      </div>
      <div>
        <label style={labelStyle}>English *</label>
        <input style={inputStyle} value={form.english} onChange={set('english')} placeholder="I like studying." required />
      </div>
      <div>
        <label style={labelStyle}>Notes</label>
        <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Optional notes..." />
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={btn('#334155', '#e2e8f0')}>Cancel</button>
        <button type="submit" disabled={saving} style={btn(saving ? '#4f46e5aa' : '#6366f1')}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

const MySentences = ({ onPractice }) => {
  const { user } = useAuthStore();
  const admin = isAdmin(user);
  const { setLessons, lessons, setCurrentLesson } = useGameStore();

  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [scope, setScope] = useState('mine'); // admin only: 'mine' | 'all'
  const [userFilter, setUserFilter] = useState('all'); // admin only: 'all' | userId

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sentencesApi.list();
      setSentences(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const created = await sentencesApi.create(form);
      setSentences([created, ...sentences]);
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, form) => {
    setSaving(true);
    try {
      const updated = await sentencesApi.update(id, form);
      setSentences(sentences.map((s) => (s.id === id ? { ...s, ...updated } : s)));
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sentence?')) return;
    try {
      await sentencesApi.remove(id);
      setSentences(sentences.filter((s) => s.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePractice = () => {
    const mySentences = admin
      ? sentences.filter((s) => s.userId === user?.id)
      : sentences;
    if (mySentences.length === 0) return;

    const lesson = userSentencesToLesson(mySentences);
    // Add/replace the custom lesson in the store, then load it
    const others = lessons.filter((l) => l.code !== 'MY-SENTENCES');
    const newLessons = [lesson, ...others];
    setLessons(newLessons);
    setCurrentLesson(0);
    onPractice?.();
  };

  // Distinct users in the data (admin only)
  const userOptions = useMemo(() => {
    const map = new Map();
    sentences.forEach((s) => {
      if (s.userId && !map.has(s.userId)) {
        map.set(s.userId, { id: s.userId, label: s.username || s.userId, count: 0 });
      }
      if (s.userId) map.get(s.userId).count += 1;
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [sentences]);

  // Apply scope (admin), user filter (admin), then text filter
  let visible = sentences;
  if (admin && scope === 'mine') {
    visible = visible.filter((s) => s.userId === user?.id);
  } else if (admin && userFilter !== 'all') {
    visible = visible.filter((s) => s.userId === userFilter);
  }
  if (filter) {
    visible = visible.filter((s) =>
      [s.chinese, s.english, s.pinyin, s.username, s.notes]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(filter.toLowerCase()))
    );
  }

  const canEdit = (s) => admin || s.userId === user?.id || !s.userId;

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px', minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '1.4rem', margin: 0 }}>
          ✏️ {admin && scope === 'all' ? 'All Sentences (Admin)' : 'My Sentences'}
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {sentences.length > 0 && (
            <button onClick={handlePractice} style={btn('#22c55e')}>
              🧩 Practice These
            </button>
          )}
          <button onClick={() => { setShowForm(true); setEditingId(null); }} style={btn('#6366f1')}>
            + Add Sentence
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          color: '#ef4444',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '0.9rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button onClick={load} style={btn('#ef4444')}>Retry</button>
        </div>
      )}

      {admin && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #334155' }}>
            <button
              onClick={() => setScope('mine')}
              style={{ ...btn(scope === 'mine' ? '#6366f1' : 'transparent', scope === 'mine' ? 'white' : '#94a3b8'), borderRadius: 0 }}
            >
              My Sentences
            </button>
            <button
              onClick={() => { setScope('all'); }}
              style={{ ...btn(scope === 'all' ? '#6366f1' : 'transparent', scope === 'all' ? 'white' : '#94a3b8'), borderRadius: 0 }}
            >
              All Users ({sentences.length})
            </button>
          </div>
          
          {scope === 'all' && userOptions.length > 0 && (
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: '180px', cursor: 'pointer' }}
            >
              <option value="all">All users ({userOptions.length})</option>
              {userOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label} ({u.count})
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      
      {(admin || sentences.length > 5) && (
        <input
          style={{ ...inputStyle, marginBottom: '16px' }}
          placeholder={admin && scope === 'all' ? 'Search text, pinyin, or username...' : 'Search sentences...'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}

      {showForm && (
        <div style={{ marginBottom: '16px' }}>
          <SentenceForm
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
            saving={saving}
          />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading sentences...</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          {sentences.length === 0
            ? 'No sentences yet. Add your first one!'
            : 'No sentences match your filter.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {visible.map((s) =>
            editingId === s.id ? (
              <SentenceForm
                key={s.id}
                initial={{ chinese: s.chinese, pinyin: s.pinyin || '', english: s.english, notes: s.notes || '' }}
                onSave={(form) => handleUpdate(s.id, form)}
                onCancel={() => setEditingId(null)}
                saving={saving}
              />
            ) : (
              <div
                key={s.id}
                style={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '12px',
                  padding: '16px 20px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1.3rem', marginBottom: '4px' }}>{s.chinese}</div>
                    {s.pinyin && <div style={{ color: '#a5b4fc', fontSize: '0.9rem', marginBottom: '2px' }}>{s.pinyin}</div>}
                    <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{s.english}</div>
                    {s.notes && <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '6px', fontStyle: 'italic' }}>{s.notes}</div>}
                    {admin && scope === 'all' && (s.username || s.userId) && (
                      <div style={{ color: s.userId === user?.id ? '#22c55e' : '#f59e0b', fontSize: '0.75rem', marginTop: '6px' }}>
                        👤 {s.username || s.userId}{s.userId === user?.id ? ' (you)' : ''}
                      </div>
                    )}
                  </div>
                  {canEdit(s) && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button onClick={() => { setEditingId(s.id); setShowForm(false); }} style={btn('#334155', '#e2e8f0')}>
                        Edit
                      </button>
                      <button onClick={() => handleDelete(s.id)} style={btn('#7f1d1d', '#fca5a5')}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MySentences;
